import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { FilterBarComponent } from "../filter-bar/filter-bar.component";
import { FilterChange, IAggregateFilterEvent, IFilterBarListWithCount, IVerticalFilterListItem } from "../filter-bar/filter-bar.models";
import "./vertical-filter-list.component.scss";

export type VerticalFilterItemCreationCallback<T> = (data: T) => IVerticalFilterListItem<T> | IVerticalFilterListItem<T>[];

@Component({
    selector: "vertical-filter-list",
    template: require("./vertical-filter-list.component.html")
})
export class VerticalFilterListComponent implements OnChanges, IFilterBarListWithCount {
    private callbackIcon?: string;

    @Input() name: string;
    @Input() items: IVerticalFilterListItem[] = [];
    @Input() multiSelect = false;
    @Input() multiSelectMode: 'every-filter' | 'some-filter' = 'every-filter';
    @Input() showChart = true;
    @Input() minValue: number;
    @Input() loading = false;

    @Input() filterBar: FilterBarComponent;
    @Input() itemCreationCallback: VerticalFilterItemCreationCallback<any>;

    @Output() change = new EventEmitter<IVerticalFilterListItem[]>();

    // ---

    filterEvent: IAggregateFilterEvent<any>;

    get selectedItems(): IVerticalFilterListItem[] {
        return this.items.filter(item => item.selected);
    }

    trackByItem = (index: number, item: IVerticalFilterListItem) => item.data;
    maxListItemCount = 0;

    constructor() {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["filterBar"] && this.filterBar) {
            this.connectFilterBar();
        }

        if (changes["items"]) {
            this.items.forEach(item => this.setIcon(item));
        }
    }

    selectItem(item: IVerticalFilterListItem): void {
        setTimeout(() => {
            this.onItemSelected(item);
        });
    }

    onItemSelected(item: IVerticalFilterListItem): void {
        if (item === null) {
            this.items.forEach((item) => item.selected = false);
        }
        else {
            if (this.multiSelect) {
                item.selected = !item.selected;
            }
            else {
                const previousSelectionState = item.selected;

                this.items.forEach((item) => item.selected = false);
                item.selected = !previousSelectionState;
            }
        }

        this.items.forEach(item => this.setIcon(item));
        this.change.emit(this.selectedItems);

        const filtersChange: FilterChange[] = this.items.map((item, index) => ({
            type: 'custom',
            event: item,
            options: { filterData: true, customName: `vertical-filter-list-${index}` }
        }));

        this.filterBar.onFiltersChange(filtersChange);
    }

    updateList(data: any[]): void {
        if (!this.itemCreationCallback) {
            return;
        }

        this.items = [];

        data.forEach(d => {
            // Does an item within our list match this current item, if so we already have this category, dont add it
            if (this.items.some(i => i.predicate(d))) {
                return;
            }

            const newItem = this.itemCreationCallback(d);
            if (!newItem) {
                return;
            }

            var items = newItem as IVerticalFilterListItem<any>[];
            if (!Array.isArray(newItem)) {
                items = [newItem];
            }

            items.forEach(item => {
                if (this.minValue && item.count < this.minValue) {
                    return;
                }

                this.callbackIcon = item.icon;

                this.setIcon(item);
                this.items.push(item);
            });
        });
    }

    // ---

    private setIcon(item: IVerticalFilterListItem): void {
        if (!item) {
            return;
        }

        const unselectedIcon = this.callbackIcon || "fa-regular fa-square";
        const selectedIcon = this.callbackIcon ? "fa-regular fa-check" : "fa-regular fa-square-check";

        if (item.selected) {
            item.icon = selectedIcon;
        }
        else {
            item.icon = unselectedIcon;
        }
    }

    private connectFilterBar(): void {
        this.filterBar.registerFilterBarList(this);
    }
}
